import { z } from "zod";

/**
 * 后台 JSON 批量导入/导出的校验模式。
 * 导入时任何时候都不信任外部 JSON：先 zod 校验，再落库。
 */

export const TIERS = [0, 1, 2] as const;

export const cardSchema = z.object({
  id: z.string().min(1).max(64),
  text: z.string().min(1).max(200),
  tags: z.array(z.string().min(1).max(24)).max(6).default([]),
  intensity: z.number().int().min(1).max(3).default(1),
  enabled: z.boolean().optional(),
});

export const cardPackSchema = z.object({
  id: z.string().min(1).max(32),
  name: z.string().min(1).max(32),
  tier: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  cards: z.array(cardSchema).min(1).max(2000),
});

export const cardsFileSchema = z.object({
  version: z.number().int().min(1).default(1),
  packs: z.array(cardPackSchema).min(1).max(50),
});

export const danmakuItemSchema = z.object({
  id: z.string().min(1).max(64),
  text: z.string().min(1).max(120),
  tags: z.array(z.string().min(1).max(24)).max(6).default([]),
  intensity: z.number().int().min(1).max(3).default(1),
  enabled: z.boolean().optional(),
  tier: z.union([z.literal(0), z.literal(1), z.literal(2)]).optional(),
});

export const danmakuFileSchema = z.object({
  version: z.number().int().min(1).default(1),
  danmaku: z.array(danmakuItemSchema).min(1).max(5000),
});

export const gameConfigSchema = z.object({
  id: z.string().min(1).max(32),
  name: z.string().min(1).max(32),
  summary: z.string().min(1).max(200),
  cover: z.string().min(1).max(200),
  players: z.object({ min: z.number().int().min(1), max: z.number().int().min(1) }),
  adult: z.boolean(),
  route: z.string().min(1).max(120),
  status: z.union([z.literal("ready"), z.literal("soon")]),
});

export const gamesFileSchema = z.object({
  version: z.number().int().min(1).default(1),
  games: z.array(gameConfigSchema).min(1).max(50),
});

export const reportSchema = z.object({
  targetType: z.union([
    z.literal("card"),
    z.literal("danmaku"),
    z.literal("room"),
    z.literal("user"),
  ]),
  targetId: z.string().min(1).max(64),
  reason: z.string().min(1).max(64),
  detail: z.string().max(500).optional(),
});

export const SITE_LINK_KINDS = [
  "x",
  "telegram",
  "qq",
  "discord",
  "bilibili",
  "weibo",
  "youtube",
  "email",
  "custom",
] as const;

/** 首页链接卡片：url 只做长度校验，真正的协议白名单在 profile.ts 的 sanitizeUrl 里 */
export const siteLinkSchema = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(SITE_LINK_KINDS),
  label: z.string().max(60).default(""),
  sublabel: z.string().max(120).default(""),
  url: z.string().max(300).default(""),
  copyText: z.string().max(120).default(""),
  enabled: z.boolean().default(true),
});

export const siteTributeSchema = z.object({
  enabled: z.boolean().default(true),
  label: z.string().max(60).default(""),
  sublabel: z.string().max(120).default(""),
  soonEnabled: z.boolean().default(false),
  soonLabel: z.string().max(60).default(""),
  soonText: z.string().max(120).default(""),
});

export const siteWorkSchema = z.object({
  id: z.string().min(1).max(64),
  title: z.string().max(60).default(""),
  sublabel: z.string().max(120).default(""),
  url: z.string().max(300).default(""),
});

/** 站点自有图片（壁纸轮换）：url 只挡长度，图片白名单在 normalizeImage 里 */
export const siteImageSchema = z.object({
  id: z.string().min(1).max(64),
  url: z.string().max(300).default(""),
  enabled: z.boolean().default(true),
});

export const priceCategorySchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(30).default(""),
});

export const priceItemSchema = z.object({
  id: z.string().min(1).max(64),
  categoryId: z.string().max(64).default(""),
  name: z.string().max(60).default(""),
  price: z.string().max(40).default(""),
  unit: z.string().max(16).default(""),
  description: z.string().max(500).default(""),
  enabled: z.boolean().default(true),
});

export const sitePriceListSchema = z.object({
  enabled: z.boolean().default(false),
  closed: z.boolean().default(false),
  closedText: z.string().max(60).default(""),
  note: z.string().max(300).default(""),
  contactNote: z.string().max(300).default(""),
  categories: z.array(priceCategorySchema).max(20).default([]),
  items: z.array(priceItemSchema).max(200).default([]),
});

export const siteGameSchema = z.object({
  id: z.string().min(1).max(32),
  name: z.string().max(32).default(""),
  summary: z.string().max(200).default(""),
  cover: z.string().max(300).default(""),
  playersMin: z.number().int().min(1).max(99).default(1),
  playersMax: z.number().int().min(1).max(99).default(2),
  adult: z.boolean().default(false),
  kind: z.union([z.literal("internal"), z.literal("external")]).default("internal"),
  /* 外链游戏要放 https 地址，站内是 /g/xxx —— 协议白名单在 normalizeGames/前台统一处理 */
  route: z.string().max(300).default(""),
  status: z.union([z.literal("ready"), z.literal("soon")]).default("ready"),
  enabled: z.boolean().default(true),
});

export const siteProfileSchema = z.object({
  siteName: z.string().max(60).default(""),
  handle: z.string().max(40).default(""),
  avatarUrl: z.string().max(300).default(""),
  signature: z.string().max(200).default(""),
  description: z.string().max(600).default(""),
  illustrationUrl: z.string().max(300).default(""),
  backgroundUrl: z.string().max(300).default(""),
  wallpapers: z.array(siteImageSchema).max(12).default([]),
  wallpaperRotate: z.boolean().default(false),
  /* 颜色：这里只挡长度，真正的白名单在 normalizeProfile 的 sanitizeColor 里
     （不合法就回落默认色，而不是整体 400，免得运营手敲一个错色值整份配置就存不进去） */
  backgroundFrom: z.string().max(64).default(""),
  backgroundTo: z.string().max(64).default(""),
  accentColor: z.string().max(64).default(""),
  tagline: z.array(z.string().max(200)).max(8).default([]),
  tags: z.array(z.string().max(24)).max(12).default([]),
  links: z.array(siteLinkSchema).max(24).default([]),
  works: z.array(siteWorkSchema).max(12).default([]),
  tribute: siteTributeSchema.default({
    enabled: true,
    label: "",
    sublabel: "",
    soonEnabled: false,
    soonLabel: "",
    soonText: "",
  }),
  priceList: sitePriceListSchema.default({
    enabled: false,
    closed: false,
    closedText: "",
    note: "",
    contactNote: "",
    categories: [],
    items: [],
  }),
});

export const settingsPatchSchema = z.object({
  adultMode: z.boolean().optional(),
  ageGate: z.boolean().optional(),
  handicapAllowed: z.boolean().optional(),
  sensitiveFilter: z.boolean().optional(),
  danmakuPerMinute: z.number().int().min(1).max(600).optional(),
  skipDailyLimit: z.number().int().min(0).max(100).optional(),
  announcement: z.string().max(500).optional(),
  announcementEnabled: z.boolean().optional(),
  /** 首页配置：后台「基础信息 / 展示区 / 联系方式 / 价格表」页整体提交 */
  profile: siteProfileSchema.optional(),
  /** 游戏大厅：后台「游戏管理」页整体提交 */
  games: z.array(siteGameSchema).max(30).optional(),
});

/**
 * 敏感词条目：既接受纯字符串（运营方直接贴一列词），
 * 也接受 `{ word, severity }`（severity: mask=打码 / block=整条拒绝）。
 */
export const sensitiveWordEntrySchema = z.union([
  z.string().min(1).max(40),
  z.object({
    word: z.string().min(1).max(40),
    severity: z.union([z.literal("mask"), z.literal("block")]).default("mask"),
  }),
]);

export const sensitiveWordsFileSchema = z.object({
  version: z.number().int().min(1).default(1),
  words: z.array(sensitiveWordEntrySchema).min(1).max(5000),
});

export interface SensitiveWordEntry {
  word: string;
  severity: "mask" | "block";
}

/** 把两种写法统一成 { word, severity } */
export function toSensitiveWordEntries(words: SensitiveWordsFileInput["words"]): SensitiveWordEntry[] {
  return words.map((entry) =>
    typeof entry === "string"
      ? { word: entry, severity: "mask" as const }
      : { word: entry.word, severity: entry.severity ?? ("mask" as const) },
  );
}

export type CardsFileInput = z.infer<typeof cardsFileSchema>;
export type DanmakuFileInput = z.infer<typeof danmakuFileSchema>;
export type GamesFileInput = z.infer<typeof gamesFileSchema>;
export type SettingsPatch = z.infer<typeof settingsPatchSchema>;
export type SiteProfileInput = z.infer<typeof siteProfileSchema>;
export type SiteLinkInput = z.infer<typeof siteLinkSchema>;
export type SensitiveWordsFileInput = z.infer<typeof sensitiveWordsFileSchema>;
